import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import {
  customFormatsApi,
  type CustomFormat,
  type QualityMediaKind,
} from "@/api/quality";
import { Button } from "@/components/ui/Button";
import {
  Field,
  NumberInput,
  Select,
  TextArea,
  TextInput,
} from "@/components/ui/Field";
import { Modal } from "@/components/ui/Modal";
import { PageHeader } from "@/components/ui/PageHeader";
import { Skeleton } from "@/components/ui/Skeleton";
import { useToast } from "@/components/ui/Toast";

/**
 * Custom-format catalog UI (issue #2). The list view groups formats by
 * media kind; an Add/Edit modal exposes a regex live-tester so users
 * can paste a release title and confirm their pattern matches before
 * saving.
 *
 * The "Curator's archive" design system: warm-near-black surfaces,
 * brass-accent for the cutoff/match indicator, JetBrains Mono for
 * regex-y stuff (patterns, scores, release titles).
 */
export function CustomFormats() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const { data, isLoading } = useQuery({
    queryKey: ["custom-formats"],
    queryFn: customFormatsApi.list,
  });

  const [editing, setEditing] = useState<CustomFormat | null>(null);
  const [creating, setCreating] = useState(false);

  const grouped = useMemo(() => {
    const out: Record<QualityMediaKind, CustomFormat[]> = {
      movie: [],
      tv: [],
      music: [],
    };
    for (const f of data ?? []) out[f.media_kind].push(f);
    return out;
  }, [data]);

  const removeMut = useMutation({
    mutationFn: (id: number) => customFormatsApi.remove(id),
    onSuccess: () => {
      toast("Custom format removed", "success");
      qc.invalidateQueries({ queryKey: ["custom-formats"] });
    },
    onError: (e) =>
      toast(e instanceof Error ? e.message : "Delete failed", "error"),
  });

  return (
    <div className="space-y-8">
      <PageHeader
        eyebrow="Settings · Quality"
        title="Custom formats"
        description="Regex patterns matched against release titles, with positive or negative scores. The grab selector picks the highest-scoring release among candidates of equal quality rank."
        actions={
          <Button onClick={() => setCreating(true)}>Add format</Button>
        }
      />

      {isLoading || !data ? (
        <Skeleton.Rows count={4} />
      ) : data.length === 0 ? (
        <section className="rounded-md border border-dashed border-line/70 bg-surface/20 px-6 py-12 text-center">
          <p className="font-display text-lg text-foreground/85">
            No custom formats yet
          </p>
          <p className="mt-1 text-sm text-foreground/55">
            Add a pattern to boost or penalise specific release types — e.g.{" "}
            <code className="font-mono text-xs text-accent/85">
              (?i)\bREMUX\b
            </code>{" "}
            for +50, or{" "}
            <code className="font-mono text-xs text-accent/85">
              (?i)\bCAM\b
            </code>{" "}
            for -10000.
          </p>
        </section>
      ) : (
        (Object.keys(grouped) as QualityMediaKind[]).map((kind) => (
          <KindSection
            key={kind}
            kind={kind}
            formats={grouped[kind]}
            onEdit={setEditing}
            onDelete={(f) => {
              if (
                confirm(
                  `Delete custom format "${f.name}"? This will also detach it from any quality profiles.`,
                )
              )
                removeMut.mutate(f.id);
            }}
          />
        ))
      )}

      {(creating || editing) && (
        <FormatModal
          format={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ["custom-formats"] });
            setCreating(false);
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}

const KIND_LABELS: Record<QualityMediaKind, string> = {
  movie: "Movies",
  tv: "TV",
  music: "Music",
};

interface KindSectionProps {
  kind: QualityMediaKind;
  formats: CustomFormat[];
  onEdit: (f: CustomFormat) => void;
  onDelete: (f: CustomFormat) => void;
}

function KindSection({ kind, formats, onEdit, onDelete }: KindSectionProps) {
  if (formats.length === 0) return null;
  return (
    <section className="overflow-hidden rounded-md border border-line bg-surface/30">
      <header className="flex items-center justify-between border-b border-line bg-surface-2/50 px-5 py-3">
        <h2 className="font-display text-lg font-medium tracking-display-tight">
          {KIND_LABELS[kind]}
        </h2>
        <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-foreground/45">
          {formats.length} format{formats.length === 1 ? "" : "s"}
        </span>
      </header>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-surface-2/30">
            <tr className="text-left">
              <th className="px-3 py-2 font-mono text-[10px] font-medium uppercase tracking-[0.12em] text-foreground/55">
                Name
              </th>
              <th className="px-3 py-2 font-mono text-[10px] font-medium uppercase tracking-[0.12em] text-foreground/55">
                Pattern
              </th>
              <th className="px-3 py-2 text-right font-mono text-[10px] font-medium uppercase tracking-[0.12em] text-foreground/55">
                Score
              </th>
              <th className="w-32 px-3 py-2" />
            </tr>
          </thead>
          <tbody className="divide-y divide-line/60">
            {formats.map((f) => (
              <tr key={f.id} className="transition hover:bg-surface-2/40">
                <td className="px-3 py-2 font-medium">{f.name}</td>
                <td className="px-3 py-2 font-mono text-xs text-foreground/75">
                  {f.pattern}
                </td>
                <td className="px-3 py-2 text-right font-mono tabular-nums">
                  <span
                    className={
                      f.score > 0
                        ? "text-good"
                        : f.score < 0
                          ? "text-bad"
                          : "text-foreground/55"
                    }
                  >
                    {f.score > 0 ? `+${f.score}` : f.score}
                  </span>
                </td>
                <td className="px-3 py-2 text-right">
                  <div className="flex justify-end gap-1.5">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onEdit(f)}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onDelete(f)}
                    >
                      Delete
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

interface FormatModalProps {
  format: CustomFormat | null;
  onClose: () => void;
  onSaved: () => void;
}

function FormatModal({ format, onClose, onSaved }: FormatModalProps) {
  const { toast } = useToast();
  const isEdit = !!format;
  const [name, setName] = useState(format?.name ?? "");
  const [pattern, setPattern] = useState(format?.pattern ?? "");
  const [score, setScore] = useState<number>(format?.score ?? 0);
  const [kind, setKind] = useState<QualityMediaKind>(
    format?.media_kind ?? "movie",
  );
  const [testTitle, setTestTitle] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Live-test the regex client-side so the user can confirm their
  // pattern matches a sample release title before saving.
  const { matches, regexError } = useMemo(() => {
    if (!pattern) return { matches: false, regexError: null as string | null };
    try {
      const re = new RegExp(pattern);
      return { matches: re.test(testTitle), regexError: null };
    } catch (e) {
      return {
        matches: false,
        regexError: e instanceof Error ? e.message : String(e),
      };
    }
  }, [pattern, testTitle]);

  const onSubmit = async () => {
    if (!name.trim()) {
      toast("Name is required", "error");
      return;
    }
    if (!pattern) {
      toast("Pattern is required", "error");
      return;
    }
    if (regexError) {
      toast(`Invalid regex: ${regexError}`, "error");
      return;
    }
    setSubmitting(true);
    try {
      if (isEdit && format) {
        await customFormatsApi.update(format.id, {
          name: name.trim(),
          pattern,
          score,
          media_kind: kind,
        });
      } else {
        await customFormatsApi.create({
          name: name.trim(),
          pattern,
          score,
          media_kind: kind,
        });
      }
      toast(
        isEdit ? "Custom format updated" : "Custom format created",
        "success",
      );
      onSaved();
    } catch (e) {
      toast(e instanceof Error ? e.message : "Save failed", "error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={isEdit ? "Edit custom format" : "New custom format"}
      eyebrow="Quality scoring"
      width="lg"
    >
      <div className="space-y-5">
        <div className="grid grid-cols-2 gap-4">
          <Field label="Name" hint="Shown in the catalog list — must be unique.">
            <TextInput
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. REMUX"
              autoFocus
            />
          </Field>
          <Field label="Media kind">
            <Select
              value={kind}
              onChange={(e) => setKind(e.target.value as QualityMediaKind)}
            >
              <option value="movie">Movies</option>
              <option value="tv">TV</option>
              <option value="music">Music</option>
            </Select>
          </Field>
        </div>

        <Field
          label="Regex pattern"
          hint="Rust regex syntax (similar to PCRE). Use (?i) at the start to make it case-insensitive."
          error={regexError ?? undefined}
        >
          <TextArea
            value={pattern}
            onChange={(e) => setPattern(e.target.value)}
            placeholder="(?i)\\bREMUX\\b"
            spellCheck={false}
            className="font-mono text-xs"
          />
        </Field>

        <Field
          label="Score"
          hint="Positive boosts matching releases; negative penalises. Typical range −10000 to +100."
        >
          <NumberInput
            value={score}
            onChange={(e) => setScore(parseInt(e.target.value || "0", 10))}
            placeholder="0"
          />
        </Field>

        <div className="rounded-md border border-line/60 bg-surface/40 p-4">
          <Field
            label="Test against a release title"
            hint="Paste any release title to see whether your pattern matches. Runs entirely in the browser — nothing is saved."
          >
            <TextInput
              value={testTitle}
              onChange={(e) => setTestTitle(e.target.value)}
              placeholder="Some.Movie.2022.REMUX.1080p.BluRay-FraMeSToR"
              spellCheck={false}
              className="font-mono text-xs"
            />
          </Field>
          {testTitle && pattern && !regexError && (
            <div
              className={
                "mt-2 flex items-center gap-2 rounded-md px-3 py-2 font-mono text-xs " +
                (matches
                  ? "border border-accent/40 bg-accent/10 text-accent"
                  : "border border-line/60 bg-surface-2/40 text-foreground/55")
              }
            >
              <span className="font-display text-[10px] uppercase tracking-[0.18em]">
                {matches ? "Match" : "No match"}
              </span>
              {matches && (
                <span className="text-accent/85">
                  +{score} score applied to releases like this
                </span>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-line pt-4">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={onSubmit} loading={submitting}>
            {isEdit ? "Save changes" : "Create format"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// Re-export so the router can lazy-load this without renaming. Mirrors
// the rest of the settings pages, all of which export a single named
// component matching the file name.
export { CustomFormats as default };
